import { createRef, RefObject } from "react";

import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { GenericResponse } from "@/Common/Api/Dto/GenericResponse";
import { IPharmacyInfo } from "@/Common/Api/Pharmacy/Dto/Responses/IPharmacyInfo";
import { SignatureClient } from "@/Common/Api/Signature/SignatureClient";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { DocumentProgressBarPopup } from "@/Common/Components/ProgressBar/DocumentProgressBarPopup";
import { SignaturePinControl } from "@/Common/Components/Sign/SignaturePinControl";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { LoginPinFrequencyModel } from "@/Common/Models/Employee/LoginFrequencyModel";
import { FileType } from "@/Common/Models/FileType";
import { MedicalRequestType } from "@/Common/Models/MedicalRequest/Enum/MedicalRequestType";
import { PatientRefIntervenorWithDetailsModel } from "@/Common/Models/Patient/PatientRefIntervenorWithDetailsModel";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { DirectoryEntityType } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { FaxDirectoryPharmacySelectorComponent } from "@/ContactDirectory/Components/FaxDirectoryPharmacySelectorComponent";
import { FaxDirectorySelectorComponent } from "@/ContactDirectory/Components/FaxDirectorySelectorComponent";
import { PhoneNumberHelper } from "@/ContactDirectory/Helpers/PhoneNumberHelper";
import { DirectoryFaxNumber } from "@/ContactDirectory/Models/DirectoryFaxNumber";
import { DirectoryFaxUpdateOptions } from "@/ContactDirectory/Models/DirectoryFaxUpdateOptions";
import { DirectoryHealthCareProvider } from "@/ContactDirectory/Models/DirectoryHealthCareProvider";
import { DirectoryEntityPatientLink, DirectoryPatientLinkUpdate } from "@/ContactDirectory/Models/DirectoryPatientLinkUpdate";
import { DirectoryPharmacy } from "@/ContactDirectory/Models/DirectoryPharmacy";
import { DirectorySelectableEntity } from "@/ContactDirectory/Models/DirectorySelectableEntity";
import { IDirectoryEntity } from "@/ContactDirectory/Models/IDirectoryEntity";
import { FilePreviewComponentEditable, FilePreviewComponentMode } from "@/ExportableFile/Components/FileSelection/FilePreviewComponentEditable";
import { ExportableFileHelper } from "@/ExportableFile/Helpers/ExportableFileHelper";
import { ExportableFileGenerationIntent } from "@/ExportableFile/Models/enum/ExportableFileGenerationIntent";
import { ExportableFile, IExportableDto } from "@/ExportableFile/Models/ExportableFile";
import { CustomFaxClient } from "../Api/CustomFaxClient";
import { ExportMode } from "../Models/enum/ExportMode";
import { ExportableFilePopupSettingModel } from "../Models/ExportableFilePopupSettingModel";
import { ExportableFileSignatureSettingsModel } from "../Models/ExportableFileSignatureSettingsModel";
import { FileTypeAttachment } from "../Models/FileTypes/FileTypeAttachment";
import { FileTypeConsultationRequest } from "../Models/FileTypes/FileTypeConsultationRequest";
import { FileTypeExamRequest } from "../Models/FileTypes/FileTypeExamRequest";
import { FileTypeRadPdf } from "../Models/FileTypes/FileTypeRadPdf";
import { DraggableImageOverlayElement } from "../Models/OverlayElements/DraggableImageOverlayElement";
import { InfoFaxReceiverContactOverlayElement } from "../Models/OverlayElements/InfoFaxReceiverContactOverlayElement";
import { DraggableImageOverlayType, OverlayElementType } from "../Models/OverlayElements/OverlayElement";
import { PrepositionedSignatureOverlayElement } from "../Models/OverlayElements/PrepositionedSignatureOverlayElement";
import { PageOptionSelector } from "../Models/PageOptionSelector";
import { ExportableFilePreFaxCallbackBuilder } from "./ExportableFileFaxPreCallback";
import { FaxCoverSheetEditorSection } from "./FaxCoverSheetEditorSection";
import { FaxPrintPageSelectionComponent } from "./FaxPrintPageSelectionComponent";
import { FaxSenderSelector } from "./FaxSenderSelector";
import { IFaxCoverSheetProps } from "./IFaxInformationProps";

import styles from "./FaxSignPrintExportableFileComponent.module.scss";

import img_loading from "@/Globals/_images/loading.gif";

type FaxSignPrintExportableFileDefaultContext = PatientRefIntervenorWithDetailsModel[] | IPharmacyInfo;

interface IFaxPrintExportableFileComponentProps {
    exportableFile: ExportableFile;
    attachedDocuments?: ExportableFile[];
    preSignedFile?: ExportableFile;
    patientContactId: string;
    mode: ExportMode;
    popupTitle?: string;
    hideSignaturePinControl?: boolean;
    isSyncToPortalAfterEnabled?: boolean;
    syncToPortalAfterChecked?: boolean;
    selectionPages?: PageOptionSelector[];
    defaultContext?: FaxSignPrintExportableFileDefaultContext;
    disableSaveSignature?: boolean;
    useSignedCopyOverride?: boolean;
    encounterId?: string;
    useLabsSearch?: boolean; // Temporary prop to allow overriding which table to search for. This will be removed when ContactDirectory project is merged.
    customCheckBox?: JSX.Element;
    customPharmacy?: IPharmacyInfo;

    hasSignOnPrintAccess?: boolean;
    hasPinSet?: boolean;
    loginPinFrequencyModel?: LoginPinFrequencyModel;
    hideInitialSignButton?: boolean;
    faxCoverSheet?: IFaxCoverSheetProps;
    fileType?: FileType;

    onClose: () => void;
    onApplyOverlaysCallback?: () => void;
    onSyncToPortalCallback?: (currentPreview: ExportableFile, unsignedFile?: string, signedFile?: string) => void;
    onPrintButtonCallback?: (currentPreview: ExportableFile) => void;
    onFaxSuccessful?: (currentPreview: ExportableFile) => void;
    onSignButtonCallback?: () => void;
    onSyncToPortalChanged?: (isChecked) => void;
    onFaxAsync?: (file: IExportableDto, signedFile: IExportableDto, faxNumber: string, faxDescription: string) => Promise<ClientResponse>
    onValidateSignatureCallback?: (exportableFile: ExportableFile) => void;
    onCustomFax?: (senderFaxNumber: string, isFaxNumberValid: boolean, mylePharmacy: DirectoryPharmacy, faxDescription: string, phoneNumber: string) => void;
    renderCustomButtons?: (shouldDisableFaxButton: boolean) => JSX.Element;
    handleFaxComplete?: () => void;
    onFaxCoverSheetChange?: (faxCoverSheet: IFaxCoverSheetProps) => void;

    selectedRecipient?: DirectorySelectableEntity<IDirectoryEntity> | string | null;
}

class FaxSignPrintExportableFileComponentState {
    public isValidPin: boolean = false;
    public isValidFax: boolean = false;
    public isSendingFax: boolean = false;
    public showCoverSheetValidationErrors: boolean = false;
    public isCoverSheetSubjectValid: boolean = true;
    public isCoverSheetMessageValid: boolean = true;
    public faxDescription: string = "";
    public faxNumber: string = "";
    public phoneNumber: string = "";
    public mylePharmacy: DirectoryPharmacy | null;
    public countryCode: string = "";
    public defaultFilePreviewComponentMode = FilePreviewComponentMode.iFramePreview;
    public currentPreviewMode = FilePreviewComponentMode.iFramePreview;
    public saveSignatureChecked: boolean = false;
    public syncToPortalAfterChecked: boolean;
    public selectionPages?: PageOptionSelector[] = [];
    public exportableFile: ExportableFile;
    public selectedFaxNumberId: string = "";

    public showSpinningWheel: boolean;

    public mergedAttachedFilesExportableFile?: ExportableFile;
    public latestGeneratedFetchUrl?: string;

    public numberOfDocumentsGenerated: number | null = null;
    public numberOfDocumentsToGenerate: number | null = null;

    public directoryEntityPatientLink: DirectoryEntityPatientLink;
    public isPreviewMode: boolean = false;
    public exportableFilePopupSetting: ExportableFilePopupSettingModel = ExportableFilePopupSettingModel.fromCacheModel(cacheStore.UserSettings.ExportableFilePopup) ?? new ExportableFilePopupSettingModel();
    public isPinFrequencyTokenValidated: boolean = false;
    public isFirstTime: boolean = cacheStore.ClinicSettings.EnableQuickDocumentSigning;
    public shouldSign: boolean = false;
    public isInitialSignButtonClicked: boolean = false;
    public isReady: boolean = false;
    public hasBeenSigned: boolean = false;
    public isFilePreviewLoaded: boolean = false;
    public isFileEditLoaded: boolean = false;

    /**
     * Whether the user has a signature in their user preferences.
     */
    public hasSignatureAccordingToPreferences: boolean = false;

    /**
     * Whether the signature position should not be saved.
     */
    public isSaveSignaturePositionDisabled: boolean = true;

    /**
     * Whether the "sign" button is disabled.
     */
    public isSignButtonDisabled: boolean = false;

    public canShowSignatureToObstetricalFileInPreviewMode: boolean = false;
    public isSignatureRemoved: boolean = false;
    public nextRecipient?: IDirectoryEntity | string;

    /**
     * Whether modifications are being applied to the file
     */
    public isFileInitializeLoading: boolean = false;

    /**
     * Whether the pin is currently being validated
     */
    public isPinValidating: boolean = false;

    /**
     * Whether the signature is currently loading on the file
     */
    public isSignatureLoadingOnFile: boolean = false;

    public constructor(fields?: Partial<FaxSignPrintExportableFileComponentState>) {
        Object.assign(this, fields);
    }
}

class FaxSignPrintExportableFileComponent extends BaseReactComponent<IFaxPrintExportableFileComponentProps, FaxSignPrintExportableFileComponentState> {
    private readonly exportableFilesRequiringSignatureToFax = [FileType.Prescription, FileType.EXAMREQUEST, FileType.CONSULTATIONREQUEST];
    private readonly faxCoverSheetEditorVisibilityModes = [ExportMode.Fax, ExportMode.UseSigned];
    private filePreviewComponentEditable: FilePreviewComponentEditable;
    private containerRef: RefObject<HTMLDivElement> = createRef();

    constructor(props: IFaxPrintExportableFileComponentProps) {
        super(props);
        this.state = new FaxSignPrintExportableFileComponentState({
            showSpinningWheel: true,
            numberOfDocumentsGenerated: 0,
            numberOfDocumentsToGenerate: null
        });
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
        const exportableFile = this.props.mode == ExportMode.UseSigned || this.props.useSignedCopyOverride
            ? this.props.preSignedFile
            : this.props.exportableFile;

        const shouldMergeAttachments = cacheStore.ClinicSettings.EnableConsultationExamRequestAttachments && this.props.attachedDocuments?.any();
        const hasSignatureAccordingToPreferences = this.hasSignatureAccordingToPreferences(this.props.mode, exportableFile);
        const canUserSignForm = this.userCanSign();

        this.mutateState(
            s => {
                if (this.props.selectedRecipient) {
                    const selectedRecipient = (this.props.selectedRecipient as DirectorySelectableEntity<IDirectoryEntity>);
                    s.faxNumber = selectedRecipient?.model?.getDefaultFaxNumberToSelect()?.number ?? "";
                    s.faxDescription = selectedRecipient?.model?.getDisplayName();
                    s.phoneNumber = selectedRecipient?.model?.getRawPhoneNumber();
                }

                s.isReady = !cacheStore.ClinicSettings.EnableQuickDocumentSigning || (this.props.exportableFile?.fileType !== FileType.RADPDF && !hasSignatureAccordingToPreferences);
                s.selectionPages = this.props.selectionPages ?? [];

                s.exportableFile = shouldMergeAttachments
                    ? null
                    : exportableFile;

                if (s.exportableFile) {
                    s.exportableFile.PageNumbers = s.selectionPages
                        .filter(p => p.checked)
                        .map(p => p.value);

                    s.isPreviewMode =
                        s.exportableFile.fileType != FileType.RADPDF
                        && s.exportableFile.fileType != FileType.WORKERCLAIM
                        && s.exportableFile.fileType != FileType.OBSTETRICALFILE
                        && s.exportableFile.fileType != FileType.WorkSafeBcForm;
                }

                if (cacheStore.ClinicSettings.EnableQuickDocumentSigning) {
                    s.syncToPortalAfterChecked = this.getAlsoSendToPortalCheckboxStatus(s.exportableFile);
                    s.saveSignatureChecked = this.getSaveSignatureCheckboxStatus(s.exportableFile);
                }
                else
                    s.syncToPortalAfterChecked = this.props.syncToPortalAfterChecked;
            },
            async () => {
                const isValidated = await this.validatePinFrequencyToken(exportableFile);

                if (cacheStore.ClinicSettings.EnableQuickDocumentSigning && !shouldMergeAttachments) {
                    const employeeFormSignResponse = this.state.exportableFile.fileType === FileType.RADPDF
                        ? await this.loadRadPdfEmployeeFormSign()
                        : { isFirstTime: true, shouldSign: false };

                    const hasSignedRadPdfBefore =
                        this.state.exportableFile.fileType === FileType.RADPDF
                        && !employeeFormSignResponse.isFirstTime
                        && employeeFormSignResponse.shouldSign;

                    this.mutateState(
                        s => {
                            s.isPinFrequencyTokenValidated = isValidated;
                            s.isFirstTime = employeeFormSignResponse.isFirstTime;
                            s.shouldSign = employeeFormSignResponse.shouldSign;
                            s.isInitialSignButtonClicked =
                                (
                                    this.state.exportableFile.fileType === FileType.RADPDF
                                    && employeeFormSignResponse.isFirstTime
                                    && isValidated
                                    && this.props.mode === ExportMode.Sign
                                )
                                || (
                                    hasSignatureAccordingToPreferences
                                    && canUserSignForm
                                    && isValidated
                                )
                                || (
                                    this.props.mode !== ExportMode.UseSigned
                                    && canUserSignForm
                                    && !isValidated
                                );
                            s.isReady = true;
                            s.showSpinningWheel = false;
                            s.hasBeenSigned = isValidated && hasSignedRadPdfBefore;
                            s.hasSignatureAccordingToPreferences = hasSignatureAccordingToPreferences || hasSignedRadPdfBefore;
                        },
                        () => {
                            if (canUserSignForm && this.state.isInitialSignButtonClicked && isValidated)
                                this.applySignatureToExportableFile("");
                        }
                    );
                }

                if (!shouldMergeAttachments)
                    return;

                // Merge attachments into one already generated file. Attachments should not changed in this specific preview. Only the original file should be changed.
                const mergedAttachmentsExportableFile = await ExportableFileHelper.mergeExportableFiles(
                    this.props.attachedDocuments,
                    mutation => this.mutateState(s => mutation(s))
                );

                if (!mergedAttachmentsExportableFile)
                    return;

                this.mutateState(s => {
                    s.mergedAttachedFilesExportableFile = mergedAttachmentsExportableFile;
                    s.exportableFile = exportableFile;

                    if (!s.exportableFile)
                        return;

                    s.saveSignatureChecked = this.getSaveSignatureCheckboxStatus(s.exportableFile);
                    s.isPinFrequencyTokenValidated = isValidated;
                    s.isFirstTime = true;
                    s.shouldSign = false;
                    s.isInitialSignButtonClicked = (
                        hasSignatureAccordingToPreferences
                        && canUserSignForm
                        && isValidated
                    )
                        || (
                            this.props.mode !== ExportMode.UseSigned
                            && canUserSignForm
                            && !isValidated
                        );

                    s.isReady = true;
                    s.showSpinningWheel = false;
                    s.hasBeenSigned = isValidated;
                    s.hasSignatureAccordingToPreferences = hasSignatureAccordingToPreferences;

                    s.exportableFile.PageNumbers = s.selectionPages
                        .filter(p => p.checked)
                        .map(p => p.value);

                    s.isPreviewMode =
                        s.exportableFile.fileType != FileType.RADPDF
                        && s.exportableFile.fileType != FileType.WORKERCLAIM
                        && s.exportableFile.fileType != FileType.OBSTETRICALFILE
                        && s.exportableFile.fileType != FileType.WorkSafeBcForm;
                }, () => {
                    if (canUserSignForm && this.state.isInitialSignButtonClicked && isValidated)
                        this.applySignatureToExportableFile("");
                });
            }
        );
    }

    public render(): JSX.Element {
        const displaySignaturesInPreviewMode =
            cacheStore.ClinicSettings.EnableQuickDocumentSigning
            && this.state.isPinFrequencyTokenValidated
            && this.userCanSign()
            && (
                this.hasSignedCurrentRadPdfFormBefore()
                || this.hasClickedSignButtonInObstetricalFile()
                || this.state.hasSignatureAccordingToPreferences
            );

        const isPrintingFromMedicalRequest = cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements
            && [FileType.EXAMREQUEST, FileType.CONSULTATIONREQUEST].includes(this.state.exportableFile?.fileType)
            && this.props.mode == ExportMode.Print;

        const showRecipientSelector = this.props.mode == ExportMode.Fax
            || this.props.mode == ExportMode.UseSigned
            || isPrintingFromMedicalRequest;

        const contactContainerClass = showRecipientSelector
            ? (cacheStore.ClinicSettings.EnableContactDirectory
                ? styles.contactContainer
                : styles.preContactDirectoryContactContainer
            )
            : "";

        return (
            <Modal
                showX
                draggable
                showOverlay
                onClose={() => this.props.onClose()}
                title={this.getPopupTitle()}
                centerContent
                top={5}
                disableOverlayClose
                style={{ minWidth: "1200px", maxWidth: "1200px", overflow: "visible" }}
                footer={this.getFooter()}
                overlayZIndex={99}
                dataCy="exportable-file-popup"
            >
                {this.state.numberOfDocumentsToGenerate > 0 &&
                    <DocumentProgressBarPopup currentValue={this.state.numberOfDocumentsGenerated} maxValue={this.state.numberOfDocumentsToGenerate} />
                }
                <div ref={this.containerRef} className={styles.container}>
                    <div className={`${styles.faxSide} ${showRecipientSelector ? styles.fullWidth : styles.thirdWidth}`}>
                        {this.state.exportableFile && this.state.isReady && (
                            <FilePreviewComponentEditable
                                file={this.state.exportableFile}
                                attachedDocuments={this.state.mergedAttachedFilesExportableFile}
                                allowEditMode={this.state.isValidPin}
                                onPreviewModeChange={mode => this.mutateState(s => s.currentPreviewMode = mode)}
                                filePreviewMode={this.state.defaultFilePreviewComponentMode}
                                exportMode={this.props.mode}
                                onFilePreviewLoaded={() => this.mutateState(s => {
                                    s.showSpinningWheel = false;
                                    s.isFilePreviewLoaded = true;
                                })}
                                onFileEditLoaded={() => this.mutateState(s => s.isFileEditLoaded = true)}
                                onApplyOverlaysCallback={hasSignature => {
                                    this.mutateState(s => s.hasBeenSigned = hasSignature);
                                    this.props.onApplyOverlaysCallback?.();
                                }}
                                onFileInitializeLoadingStateChange={(isLoading, callback) => {
                                    this.mutateState(s => s.isFileInitializeLoading = isLoading, callback);
                                }}
                                onUpdateFetchUrl={url => this.mutateState(s => s.latestGeneratedFetchUrl = url)}
                                ref={ref => this.filePreviewComponentEditable = ref}
                                includeSignatureInPreviewMode={displaySignaturesInPreviewMode}
                                onOverlaysChange={(draggableOverlays, prepositionedSignatureOverlays) => this.handleOverlaysChange(draggableOverlays, prepositionedSignatureOverlays)}
                                documentPages={this.state.selectionPages?.map(p => p.value)}
                                changePreviewModeWhenSignatureMalicious={() => this.mutateState(s => { s.isPreviewMode = false; s.isInitialSignButtonClicked = true; })}
                                isSignatureRemoved={this.state.isSignatureRemoved}
                                dataCy="exportable-file-preview"
                            />
                        )}
                    </div>
                    <div className={contactContainerClass}>
                        {(showRecipientSelector) && (
                            <FeatureFlagControl
                                flag={cacheStore.ClinicSettings.EnableContactDirectory}
                                activeFeature={this.state.exportableFile?.fileType === FileType.Prescription
                                    ? (
                                        <FaxDirectoryPharmacySelectorComponent
                                            customPharmacy={this.props.customPharmacy}
                                            patientContactId={this.props.patientContactId}
                                            isFaxValid={isValid => this.mutateState(s => s.isValidFax = isValid)}
                                            onFaxUpdate={options => this.mutateState(s => {
                                                if (!s.exportableFile)
                                                    return;

                                                s.countryCode = options.countryCode;

                                                const pharmacy = options.pharmacy;
                                                const directoryPharmacy = options.selectedEntity as DirectoryPharmacy;
                                                const shouldSavePatientLinkAsCustomPharmacy = (
                                                    IsNullOrWhiteSpace(directoryPharmacy?.banner)
                                                    || IsNullOrWhiteSpace(directoryPharmacy?.pharmacists)
                                                    || IsNullOrWhiteSpace(directoryPharmacy?.faxNumbers?.firstOrDefault()?.number)
                                                );

                                                const isSendToMylePharmacyEnabled = cacheStore.ClinicSettings.EnableSendPrescriptionToMylePharmacy;
                                                if (
                                                    !isSendToMylePharmacyEnabled
                                                    || (isSendToMylePharmacyEnabled && !!pharmacy.Name && !!pharmacy.Fax)
                                                ) {
                                                    s.exportableFile.applyPrepositionedPharmacyInfoOverlay(pharmacy, options.shouldUpdatePatientLink, options.stateCode !== "CA-QC");
                                                }

                                                if (options.shouldUpdatePatientLink) {
                                                    s.directoryEntityPatientLink = new DirectoryEntityPatientLink({
                                                        customPharmacy: shouldSavePatientLinkAsCustomPharmacy ? pharmacy : null,
                                                        directoryEntity: shouldSavePatientLinkAsCustomPharmacy ? null : directoryPharmacy,
                                                        entityType: DirectoryEntityType.Pharmacy,
                                                        patientContactId: this.props.patientContactId
                                                    });
                                                }
                                                else {
                                                    s.directoryEntityPatientLink = null;
                                                }

                                                s.faxNumber = pharmacy.Fax;
                                                s.phoneNumber = pharmacy.Phone;

                                                const mylePharmacy = directoryPharmacy?.isMylePharmacy
                                                    ? directoryPharmacy
                                                    : null;
                                                s.mylePharmacy = mylePharmacy;

                                                s.exportableFile.showOverlayRefreshButton = s.isValidFax;
                                                s.faxDescription = this.getMylePharmacyFaxDescription(mylePharmacy, pharmacy);
                                                s.selectedFaxNumberId = options.selectedFaxNumberId;

                                                if (this.props.onCustomFax) {
                                                    this.props.onCustomFax(s.faxNumber, s.isValidFax, mylePharmacy, s.faxDescription, s.phoneNumber);
                                                }
                                            })}
                                        />
                                    )
                                    : (
                                        <FaxDirectorySelectorComponent
                                            onFaxUpdate={(option: DirectoryFaxUpdateOptions) => this.mutateState(s => {
                                                s.faxNumber = option.faxNumber;
                                                if (!option.selectedFaxNumberId || s.selectedFaxNumberId !== option.selectedFaxNumberId) {
                                                    s.faxDescription = option.description;
                                                }

                                                s.selectedFaxNumberId = option.selectedFaxNumberId;
                                                s.countryCode = option.countryCode;
                                                s.phoneNumber = option.selectedEntity?.getRawPhoneNumber();

                                                s.nextRecipient = option.selectedEntity ?? option.description;

                                                if (!s.exportableFile)
                                                    return;

                                                const fax = PhoneNumberHelper.formatDirectoryNumber(
                                                    new DirectoryFaxNumber({
                                                        number: option.faxNumber,
                                                        countryId: cacheStore.getCountryMedfarDictionariesByLookUpCode(
                                                            cacheStore.GetCountryLookupCodeByISOCodeAlpha2(option.countryCode)
                                                        )?.Id
                                                    })
                                                );

                                                s.exportableFile = s.exportableFile
                                                    .applyDistinctOverlayElements(
                                                        [new InfoFaxReceiverContactOverlayElement({
                                                            FaxNumber: fax,
                                                            Name: option.description,
                                                            RecipientContactDirectoryEntityId: option.selectedEntity?.id,
                                                            RecipientContactDirectoryEntityType: option.selectedEntity?.type
                                                        })],
                                                        OverlayElementType.InfoFaxReceiverContact
                                                    );

                                                s.exportableFile.showOverlayRefreshButton = [FileType.EXAMREQUEST, FileType.CONSULTATIONREQUEST].includes(this.state.exportableFile?.fileType)
                                                    && s.isValidFax;

                                                if (option.shouldUpdatePatientLink && option.entityType === DirectoryEntityType.HealthcareProvider)
                                                    s.directoryEntityPatientLink = new DirectoryEntityPatientLink({
                                                        directoryEntity: option.selectedEntity as DirectoryHealthCareProvider,
                                                        entityType: option.entityType,
                                                        selectedFaxNumberId: option.selectedFaxNumberId,
                                                        patientContactId: this.props.patientContactId
                                                    });
                                            })}
                                            patientContactId={this.props.patientContactId}
                                            isFaxValid={isValid => this.mutateState(s => s.isValidFax = isValid)}
                                            enableUpdateExternalConsultantFaxNumberCheckbox
                                            defaultSelectedRecipient={this.props.selectedRecipient}
                                        />
                                    )
                                }
                                fallbackFeature={this.state.exportableFile && (
                                    <FaxSenderSelector
                                        exportableFile={this.state.exportableFile}
                                        state={this.state}
                                        mutateState={(mutation, then) => this.mutateState(
                                            s => {
                                                mutation(s);
                                                const context = this.props.defaultContext as IPharmacyInfo;
                                                this.props.onCustomFax?.(s.faxNumber, s.isValidFax, s.mylePharmacy, this.getMylePharmacyFaxDescription(s.mylePharmacy, context), s.phoneNumber || context?.Phone);
                                            },
                                            () => then?.()
                                        )}
                                        defaultContext={this.props.defaultContext}
                                        showSpinningWheel={this.state.showSpinningWheel}
                                        useLabsSearch={this.props.useLabsSearch}
                                        customPharmacy={this.props.customPharmacy}
                                        patientContactId={this.props.patientContactId}
                                    />
                                )}
                            />
                        )}
                        <FaxPrintPageSelectionComponent
                            selectionPages={this.state.selectionPages}
                            onSelectedPageClick={page => this.onSelectedPageClick(page)}
                        />
                        {this.hasCoverSheetSection() && (
                            <FaxCoverSheetEditorSection
                                isSubjectValid={this.state.showCoverSheetValidationErrors ? this.state.isCoverSheetSubjectValid : true}
                                isMessageValid={this.state.showCoverSheetValidationErrors ? this.state.isCoverSheetMessageValid : true}
                                validate={(isSubjectValid, isMessageValid) => this.mutateState(s => {
                                    s.isCoverSheetSubjectValid = isSubjectValid;
                                    s.isCoverSheetMessageValid = isMessageValid;
                                })}
                                containerClassName="margin-lg-top"
                                subject={this.props.faxCoverSheet?.CoverPageTitle}
                                message={this.props.faxCoverSheet?.CoverPageContent}
                                onSubjectChange={text => {
                                    this.mutateState(s => s.showCoverSheetValidationErrors = false);
                                    this.props.onFaxCoverSheetChange?.({ CoverPageTitle: text });
                                }}
                                onMessageChange={text => {
                                    this.mutateState(s => s.showCoverSheetValidationErrors = false);
                                    this.props.onFaxCoverSheetChange?.({ CoverPageContent: text });
                                }}
                                onCheckboxChange={value => {
                                    this.mutateState(s => s.showCoverSheetValidationErrors = false);
                                    this.props.onFaxCoverSheetChange?.({ GenerateCoverPage: value });
                                }}
                            />
                        )}
                    </div>

                </div>
            </Modal>
        );
    }

    private getMylePharmacyFaxDescription(directoryPharmacy: DirectoryPharmacy, pharmacy?: IPharmacyInfo): string {
        return directoryPharmacy ? [directoryPharmacy.banner, directoryPharmacy.pharmacists].filter(p => !IsNullOrWhiteSpace(p)).join("-")
            : [pharmacy.Banner, pharmacy.Name].filter(p => !IsNullOrWhiteSpace(p)).join("-");
    }

    private getFooter(): JSX.Element {
        const hasSignOnPrintAccess = this.getHasSignOnPrintAccess();
        const canUserSign = this.userCanSign();

        const showInitialSignButton =
            cacheStore.ClinicSettings.EnableQuickDocumentSigning
            && cacheStore.ClinicSettings.EnableSignedAuthorization
            && this.props.mode !== ExportMode.UseSigned
            && !this.props.useSignedCopyOverride
            && (!canUserSign || !this.state.isInitialSignButtonClicked)
            && !this.props.hideInitialSignButton;

        const showSignaturePinTextbox =
            !this.state.isValidPin
            && !this.state.isPinFrequencyTokenValidated
            && hasSignOnPrintAccess
            && this.props.mode != ExportMode.UseSigned
            && !this.props.useSignedCopyOverride
            && !this.props.hideSignaturePinControl
            && !showInitialSignButton;

        const showRemoveSignatureButton =
            cacheStore.ClinicSettings.EnableQuickDocumentSigning
            && cacheStore.ClinicSettings.EnableSignedAuthorization
            && this.hasSignatureAccordingToPreferences(this.props.mode, this.state.exportableFile)
            && !!this.getSignaturePreferencesProperty(this.state.exportableFile)
            && !showSignaturePinTextbox
            && !showInitialSignButton
            && !this.props.useSignedCopyOverride
            && canUserSign;

        const showSaveSignatureCheckbox =
            cacheStore.ClinicSettings.EnableSignedAuthorization
            && !this.props.disableSaveSignature
            && this.props.mode != ExportMode.Sign
            && hasSignOnPrintAccess
            && this.props.mode != ExportMode.UseSigned
            && !this.props.useSignedCopyOverride
            && !this.props.hideSignaturePinControl;

        const showSignatureButtons =
            !this.exportableFilesRequiringSignatureToFax.includes(this.state.exportableFile?.fileType)
            && cacheStore.ClinicSettings.EnableQuickDocumentSigning
            && (this.state.isValidPin || this.state.isPinFrequencyTokenValidated)
            && !this.props.useSignedCopyOverride
            && !this.props.hideSignaturePinControl
            && !showInitialSignButton;

        return (
            <div className={styles.footerContainer}>
                <div className={styles.footerControlsContainer}>
                    {showInitialSignButton && (
                        <ButtonControl
                            className="btn btn-default"
                            label={this.hasSignedBeforeAndCanSign() ? cacheStore.GetString("EDITLabel") : cacheStore.GetString("SignLabel")}
                            disabled={!this.state.isFilePreviewLoaded}
                            onClick={() => this.onInitialSignButtonClick()}
                        />
                    )}

                    {showRemoveSignatureButton && (
                        <ButtonControl
                            className="btn btn-default"
                            dataCy="faxSignPrintExportableFile-deleteSignature"
                            label={cacheStore.GetString("DocumentSigningPinOptions_DeleteSignatureLabel")}
                            disabled={this.state.exportableFile.overlayElements.length < 0}
                            onClick={() => this.onRemoveSignatureButtonClick()}
                        />
                    )}

                    {showSignaturePinTextbox && this.state.isFilePreviewLoaded && (
                        <SignaturePinControl
                            pinWarningRenderRight
                            disableAutoFocus={this.props.attachedDocuments?.any()}
                            disableControl={!this.state.isFilePreviewLoaded}
                            dataCy="faxSignPrintExportableFile-signaturePin"
                            onSuccessFunction={pin => {
                                this.applySignatureToExportableFile(
                                    pin,
                                    this.hasNoSignaturePreferencesOrIsNotRadPdf()
                                );

                                if (cacheStore.ClinicSettings.EnableQuickDocumentSigning) {
                                    this.refreshPinFrequencyToken(pin);
                                }
                            }}
                            onRejectFunction={() => null}
                            onLoadingStateChange={(isPinValidating, callback) => {
                                this.mutateState(
                                    s => {
                                        s.isPinValidating = isPinValidating;
                                    },
                                    callback
                                );
                            }}
                        />
                    )}

                    {showSignatureButtons && (
                        <>
                            {this.state.isPreviewMode && (
                                <ButtonControl
                                    className="btn btn-default"
                                    label={cacheStore.GetString("EDITLabel")}
                                    disabled={!this.state.isFilePreviewLoaded}
                                    onClick={() => {
                                        this.filePreviewComponentEditable?.switchToEditMode();
                                        this.mutateState(prevState => {
                                            prevState.isPreviewMode = !this.state.isPreviewMode;
                                            prevState.isFilePreviewLoaded = false;
                                            prevState.canShowSignatureToObstetricalFileInPreviewMode = false;
                                        });
                                    }}
                                />
                            )}
                            {!this.state.isPreviewMode && (
                                <>
                                    <ButtonControl
                                        className="btn btn-primary"
                                        label={cacheStore.GetString("DocumentSigningPinOptions_AddSignatureLabel")}
                                        disabled={!this.state.isFileEditLoaded}
                                        onClick={() => this.filePreviewComponentEditable?.onSignButton()}
                                    />
                                    <ButtonControl
                                        className="btn btn-default"
                                        label={cacheStore.GetString("DocumentSigningPinOptions_PreviewLabel")}
                                        disabled={!this.state.isFileEditLoaded}
                                        onClick={() => {
                                            this.filePreviewComponentEditable?.applyOverlaysAndSwitchToPreviewMode();
                                            this.mutateState(prevState => {
                                                prevState.isPreviewMode = !this.state.isPreviewMode;
                                                prevState.isFileEditLoaded = false;
                                                prevState.canShowSignatureToObstetricalFileInPreviewMode = true;
                                            });
                                        }}
                                    />
                                </>
                            )}
                        </>
                    )}

                    <div className={styles.footerCheckboxesContainer}>
                        {showSaveSignatureCheckbox && (
                            <div className={styles.footerCheckboxLabel}>
                                <input
                                    type="checkbox"
                                    id="chkSaveSignature"
                                    onChange={() => this.onSaveSignatureChanged()}
                                    checked={this.state.saveSignatureChecked}
                                    name="SaveSignatureCheckbox"
                                    disabled={this.state.isSaveSignaturePositionDisabled}
                                />
                                <label
                                    htmlFor="SaveSignatureCheckbox"
                                    onClick={() => this.onSaveSignatureChanged()}
                                >
                                    {cacheStore.GetString("SaveSignatureLabel")}
                                </label>
                            </div>
                        )}
                        {this.props.isSyncToPortalAfterEnabled && (
                            <div className={styles.footerCheckboxLabel}>
                                <input
                                    type="checkbox"
                                    id="chkAlsoSendToPortal"
                                    onChange={() => this.onSyncToPortalChanged()}
                                    checked={this.state.syncToPortalAfterChecked}
                                    name="chkAlsoSendToPortal"
                                />
                                <label
                                    htmlFor="chkAlsoSendToPortal"
                                    onClick={() => this.onSyncToPortalChanged()}
                                >
                                    {cacheStore.GetString("AlsoSendToPortalLabel")}
                                </label>
                            </div>
                        )}
                        {this.props.customCheckBox}
                    </div>

                    {this.state.showSpinningWheel && <img src={img_loading} alt="loading" />}
                </div>
                <div className="flex-container flex-col-gap-xs">
                    {this.props.renderCustomButtons?.(this.isFaxButtonDisabled()) ?? (
                        <>
                            <ButtonControl
                                className="btn btn-default"
                                dataCy="faxSignPrintExportableFile-cancel"
                                label={cacheStore.GetString("CancelLabel")}
                                onClick={() => this.props.onClose()}
                            />

                            {this.props.mode == ExportMode.UseSigned && this.props.isSyncToPortalAfterEnabled && this.props.onSyncToPortalCallback && (
                                <PortalStatusButton
                                    disabled={!this.state.isFilePreviewLoaded}
                                    onClick={() => {
                                        this.onSyncToPortalCallback(true);
                                        this.props.onClose();
                                    }}
                                    {...PortalHelper.getPortalButtonPropsForSyncStatus(PortalSyncStatus.Unsent, false)}
                                />
                            )}
                            {(this.props.mode == ExportMode.Print || this.props.mode == ExportMode.UseSigned) && (
                                <ButtonControl
                                    className="btn btn-primary"
                                    label={cacheStore.GetString("PrintLabel")}
                                    disabled={this.isPrintButtonDisable()}
                                    onClick={() => {
                                        this.persistSignatureIfSaveSignatureIsChecked(async () => {
                                            if (cacheStore.ClinicSettings.EnableQuickDocumentSigning && this.state.defaultFilePreviewComponentMode === FilePreviewComponentMode.imageEdit)
                                                this.filePreviewComponentEditable.updateAllOverlayElementsData();

                                            if (cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements) {
                                                await this.saveUpdatedRecipient();
                                            }

                                            const exportableFile = await this.getExportableFileOnEditMode();
                                            if (cacheStore.ClinicSettings.EnableConsultationExamRequestAttachments && this.props.attachedDocuments?.any())
                                                PrintHelper.ShowPrintPreview(await this.getRequestWithAttachedDocumentUrl(true, ExportableFileGenerationIntent.Print));
                                            else {
                                                const isFinalFile =
                                                    this.state.exportableFile.fileType === FileType.Prescription
                                                    || this.state.exportableFile.fileType === FileType.CONSULTATIONREQUEST;

                                                await exportableFile.print(this.state.hasBeenSigned, isFinalFile, ExportMode.Print, ExportableFileGenerationIntent.Print);
                                            }

                                            this.props.onClose();
                                            this.props.onPrintButtonCallback?.(exportableFile);
                                            this.onSyncToPortalCallback();

                                            if (cacheStore.ClinicSettings.EnableQuickDocumentSigning) {
                                                this.updateExportableFilePopupSetting(this.state.exportableFilePopupSetting);
                                                this.saveRadPdfFormAction(ExportMode.Print);
                                            }
                                        });
                                    }}
                                />
                            )}
                            {(this.props.mode == ExportMode.Fax || this.props.mode == ExportMode.UseSigned) && sessionStore.faxAccess == true && (
                                <ButtonControl
                                    className="btn btn-primary"
                                    label={cacheStore.GetString("SendFaxLabel")}
                                    disabled={this.isFaxButtonDisabled()}
                                    onClick={() => {
                                        this.sendFax(async () => {
                                            this.persistSignatureIfSaveSignatureIsChecked(() => this.onSyncToPortalCallback());

                                            if (cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements) {
                                                await this.saveUpdatedRecipient();
                                            }

                                            if (cacheStore.ClinicSettings.EnableQuickDocumentSigning) {
                                                this.updateExportableFilePopupSetting(this.state.exportableFilePopupSetting);
                                                this.saveRadPdfFormAction(ExportMode.Fax);
                                            }
                                        });
                                    }}
                                />
                            )}
                            {this.props.mode == ExportMode.Sign && (
                                <ButtonControl
                                    className="btn btn-primary"
                                    dataCy="faxSignPrintExportableFile-sign"
                                    label={cacheStore.GetString("SignLabel")}
                                    disabled={this.isSignButtonDisable()}
                                    onClick={() => this.persistSignature(() => {
                                        this.props.onClose();
                                        this.onSyncToPortalCallback();

                                        if (cacheStore.ClinicSettings.EnableQuickDocumentSigning) {
                                            this.updateExportableFilePopupSetting(this.state.exportableFilePopupSetting);
                                            this.saveRadPdfFormAction(ExportMode.Sign);
                                        }
                                    })}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    }

    private isFaxButtonDisabled(): boolean {
        if ((this.hasPreferenceAndNotSetupToSignYet() && !this.props.useSignedCopyOverride)
            || !this.state.isValidFax
            || this.state.isSendingFax
            || this.state.isFileInitializeLoading
            || this.state.isSignatureLoadingOnFile
            || this.state.isPinValidating
        )
            return true;

        const isInEditMode = this.state.currentPreviewMode === FilePreviewComponentMode.imageEdit;
        return (
            (isInEditMode && cacheStore.ClinicSettings.EnableQuickDocumentSigning
                ? !this.state.isFileEditLoaded
                : !this.state.isFilePreviewLoaded
            )
            || (
                !cacheStore.ClinicSettings.EnableQuickDocumentSigning
                && (
                    this.state.currentPreviewMode == FilePreviewComponentMode.imageEdit
                    || (
                        this.exportableFilesRequiringSignatureToFax.contains(this.state.exportableFile.fileType)
                        && !this.state.exportableFile?.hasSignature()
                        && this.props.mode != ExportMode.UseSigned
                    )
                )
            )
        );
    }

    private async onSyncToPortalCallback(overrideCheckbox: boolean = false): Promise<void> {
        if (!this.props.isSyncToPortalAfterEnabled || (!overrideCheckbox && !this.state.syncToPortalAfterChecked))
            return;

        const fileNames = await this.getExportableFileUnSignedAndSignedName();
        this.props.onSyncToPortalCallback?.(this.state.exportableFile, fileNames[0], fileNames[1]);
    }

    private async getExportableFileUnSignedAndSignedName(): Promise<string[]> {
        const result = await Promise.all([
            this.state.exportableFile.getFetchUrlDtoAsync(false),
            this.state.exportableFile.getFetchUrlDtoAsync(true)
        ]);

        return result.map(p => MyleHelper.extractFileNameFromUrl(p.FetchUrl));
    }

    private onSyncToPortalChanged(): void {
        this.mutateState(
            s => {
                if (cacheStore.ClinicSettings.EnableQuickDocumentSigning) {
                    const type = this.state.exportableFile?.fileType;
                    const field = ExportableFilePopupSettingModel.getFieldFor(s.exportableFilePopupSetting, type);

                    if (field)
                        field.isAlsoSendToPortal = !field.isAlsoSendToPortal;
                }

                s.syncToPortalAfterChecked = !s.syncToPortalAfterChecked;
            },
            () => this.props.onSyncToPortalChanged?.(this.state.syncToPortalAfterChecked)
        );
    }

    private onSaveSignatureChanged(): void {
        if (this.state.isSaveSignaturePositionDisabled)
            return;

        this.mutateState(s => {
            if (cacheStore.ClinicSettings.EnableQuickDocumentSigning) {
                const type = this.state.exportableFile?.fileType;
                const field = ExportableFilePopupSettingModel.getFieldFor(s.exportableFilePopupSetting, type);

                if (field)
                    field.isSaveSignature = !field.isSaveSignature;
            }

            s.saveSignatureChecked = !s.saveSignatureChecked;
        });
    }

    private sendFax(then: () => void = null): void {
        const stateOnFaxClick = { ...this.state };
        if (cacheStore.ClinicSettings.EnableQuickDocumentSigning && this.state.defaultFilePreviewComponentMode === FilePreviewComponentMode.imageEdit)
            this.filePreviewComponentEditable.updateAllOverlayElementsData();

        if (this.props.faxCoverSheet?.GenerateCoverPage && (!this.state.isCoverSheetMessageValid || !this.state.isCoverSheetSubjectValid)) {
            this.mutateState(s => s.showCoverSheetValidationErrors = true);
            return;
        }

        this.mutateState(
            s => {
                s.showSpinningWheel = true;
                s.isSendingFax = true;
            },
            async () => {
                const executeAfterFax = then;
                then = () => {
                    this.mutateState(s => s.isSendingFax = false);
                    executeAfterFax?.();
                };

                const cleanExportableFile = await this.getExportableFileOnEditMode(ExportableFileGenerationIntent.Fax);

                const isFinalFile =
                    this.state.exportableFile.fileType === FileType.Prescription
                    || this.state.exportableFile.fileType === FileType.CONSULTATIONREQUEST;

                const filePromise = cleanExportableFile?.toFileDto(true, isFinalFile, ExportMode.Fax, ExportableFileGenerationIntent.Fax);

                // used for custom preferred pharmacy
                await ExportableFilePreFaxCallbackBuilder
                    .buildFor(cleanExportableFile?.fileType)
                    ?.trigger(cleanExportableFile, this.props.patientContactId);

                if (cacheStore.ClinicSettings.EnableContactDirectory) {
                    await DirectoryPatientLinkUpdate.trigger(stateOnFaxClick.directoryEntityPatientLink);
                    this.mutateState(s => s.directoryEntityPatientLink = null);
                }

                // check if a signature overlay was just added
                if (cleanExportableFile?.hasSignature()) {
                    // Create a copy without signature
                    const unSignedFilePromise = cleanExportableFile
                        .clone(true)
                        .toFileDto(true, true, ExportMode.Fax);

                    const result = await Promise.all([unSignedFilePromise, filePromise]);
                    this.Fax(stateOnFaxClick, result[0], result[1], then);
                }
                else { // unsigned or pre-signed file. pre-signed file will always be re-faxable with signature already applied
                    const file = await filePromise;
                    this.Fax(stateOnFaxClick, file, null, then);
                }
            }
        );
    }

    private persistSignatureIfSaveSignatureIsChecked(callback: () => void = null): void {
        if (!this.state.saveSignatureChecked
            || !this.state.exportableFile.hasSignature()
        ) {
            callback?.();
            return;
        }

        // generate signed file if needed
        this.persistSignature(callback);
    }

    private persistSignature(callback: () => void = null): void {
        // generate signed file if needed
        this.mutateState(
            s => {
                s.showSpinningWheel = true;
                s.isSignatureLoadingOnFile = true;
            },
            async () => {
                try {
                    const exportableFile = await this.getExportableFileOnEditMode();
                    await exportableFile.persistSignature(this.props.encounterId);
                }
                catch (error: any) {
                    throw error;
                }
                finally {
                    this.mutateState(s => {
                        s.isSignatureLoadingOnFile = false;
                    });
                }
                this.props.onSignButtonCallback?.();
                callback?.();
            }
        );
    }

    private async Fax(stateOnFaxClick: FaxSignPrintExportableFileComponentState, file: IExportableDto, signedFile: IExportableDto, then: () => void): Promise<void> {
        const mergedFiles = cacheStore.ClinicSettings.EnableConsultationExamRequestAttachments && this.props.attachedDocuments?.any()
            ? stateOnFaxClick.mergedAttachedFilesExportableFile.getAttachedObject_ObjectId()
            : null;

        let response: ClientResponse;

        if (!this.props.onFaxAsync) {
            const mylePharmacy = this.state.mylePharmacy?.isMylePharmacy
                ? {
                    ClientTag: this.state.mylePharmacy.mylePharmacyClientTag,
                    Banner: this.state.mylePharmacy.banner,
                    Pharmacists: this.state.mylePharmacy.pharmacists,
                    Address: {
                        StreetAddress: this.state.mylePharmacy.address.streetAddress,
                        City: this.state.mylePharmacy.address.city,
                        PostalCode: this.state.mylePharmacy.address.postalCode,
                        StateId: this.state.mylePharmacy.address.stateId,
                        CountryId: this.state.mylePharmacy.address.countryId
                    }
                }
                : null;

            const coverFaxCoverPageParams = this.hasCoverSheetSection()
                ? this.props.faxCoverSheet
                : null;

            response = await Client.postAsync(
                ServerOperations.Server.Myle_Faxing_SendExportableFileFax,
                {
                    CountryCode: stateOnFaxClick.countryCode,
                    ExportableFileDtoSigned: signedFile,
                    ExportableFileDto: file,
                    PatientContactId: this.props.patientContactId,
                    FaxNumber: this.state.faxNumber,
                    FaxNumberDescription: this.state.faxDescription,
                    RecipientPhoneNumber: this.state.phoneNumber,
                    PageNumbers: this.state.selectionPages?.filter(p => p.checked).map(p => p.value),
                    AttachedDocumentFileName: mergedFiles,
                    MylePharmacy: mylePharmacy,
                    PdfCoverPageParams: coverFaxCoverPageParams
                }
            );
        }
        else {
            response = await this.props.onFaxAsync(file, signedFile, this.state.faxNumber, this.state.faxDescription);
        }

        if (!response.isSuccess)
            sessionStore.showPopup(response.errors);

        then?.();

        stateOnFaxClick.syncToPortalAfterChecked && this.props.onFaxSuccessful?.(stateOnFaxClick.exportableFile);

        this.props.handleFaxComplete?.();
        this.props.onClose();
    }

    private onSelectedPageClick(page: PageOptionSelector): void {
        this.mutateState(s => {
            let checkedValue: boolean = null;
            s.selectionPages.forEach(p => {
                if (p.value === page.value)
                    checkedValue = p.checked = !p.checked;
            });

            if (!IsNullOrUndefined(page.pageLink))
                s.selectionPages.mutate(p => p.checked = checkedValue, p => p.value === page.pageLink);

            const currentDraggableOverlayElement = s.exportableFile.draggableImageOverlayElement;
            s.exportableFile = s.exportableFile.getCleanCopy();
            s.exportableFile.PageNumbers = s.selectionPages.filter(p => p.checked).map(p => p.value);

            for (const overlay of currentDraggableOverlayElement.filter(p => s.exportableFile.PageNumbers.contains(p.page)))
                s.exportableFile.applyOverlayElement(overlay);
        });
    }

    private getPopupTitle(): string {
        if (this.props.popupTitle)
            return this.props.popupTitle;

        switch (this.props.mode) {
            case ExportMode.Sign:
                return this.getLabelForTypeFile("Sign");

            case ExportMode.Fax:
                return this.getLabelForTypeFile("Fax");

            case ExportMode.Print:
                return this.getLabelForTypeFile("Print");

            case ExportMode.UseSigned:
                return cacheStore.GetString("UseSignedCopyLabel");
        }
    }

    private getLabelForTypeFile(exportmode: string): string {
        switch (this.props.exportableFile.fileType) {
            case FileType.EXAMREQUEST:
                return cacheStore.GetString(exportmode + "ExamRequestTitleLabel");
            case FileType.CONSULTATIONREQUEST:
                return cacheStore.GetString(exportmode + "ConsultationRequestTitleLabel");
            case FileType.Prescription:
                return cacheStore.GetString(exportmode + "PrescriptionTitleLabel");
            case FileType.ENCOUNTER:
                return cacheStore.GetString(exportmode + "MedicalNoteTitleLabel");
            case FileType.RADPDF:
            case FileType.WORKERCLAIM:
            case FileType.OBSTETRICALFILE:
                return cacheStore.GetString(exportmode + "FormTitleLabel");
            default:
                return cacheStore.GetString(exportmode + "DocumentsLabel");
        }
    }

    private async getRequestWithAttachedDocumentUrl(
        isFinalFile: boolean,
        generationIntent: ExportableFileGenerationIntent = ExportableFileGenerationIntent.Preview
    ): Promise<string | null> {
        if (!this.state.exportableFile)
            return null;

        if (this.state.latestGeneratedFetchUrl)
            return this.state.latestGeneratedFetchUrl;

        const exportableFile: ExportableFile = await ExportableFileHelper.getConcatenatedFetchUrlDtoAsync(
            this.state.exportableFile,
            this.props.attachedDocuments,
            null,
            null,
            isFinalFile,
            generationIntent
        );

        return (await exportableFile?.getFetchUrlDtoAsync())?.FetchUrl;
    }

    private getSaveSignatureCheckboxStatus(exportableFile?: ExportableFile): boolean {
        const type = exportableFile?.fileType ?? this.state.exportableFile?.fileType;
        const field = ExportableFilePopupSettingModel.getFieldFor(this.state.exportableFilePopupSetting, type);

        return field?.isSaveSignature ?? false;
    }

    private getAlsoSendToPortalCheckboxStatus(exportableFile?: ExportableFile): boolean {
        const type = exportableFile?.fileType ?? this.state.exportableFile?.fileType;
        const field = ExportableFilePopupSettingModel.getFieldFor(this.state.exportableFilePopupSetting, type);

        return field?.isAlsoSendToPortal ?? false;
    }

    private async saveRadPdfFormAction(actionType: ExportMode): Promise<boolean> {
        const exportableFileType = this.state.exportableFile.fileType;

        if (
            !cacheStore.ClinicSettings.EnableQuickDocumentSigning
            || !(exportableFileType === FileType.RADPDF || exportableFileType === FileType.ATTACHMENT)
        ) {
            return false;
        }

        const formId = (this.state.exportableFile as FileTypeRadPdf).formId ?? (this.state.exportableFile as FileTypeAttachment).radPdfFormId;

        return await CustomFaxClient.saveEmployeeFormSign({
            EmployeeId: sessionStore.getCurrentEmployee()?.ContactId,
            FormId: formId,
            ActionType: actionType,
            ShouldSign: this.state.hasBeenSigned || this.props.useSignedCopyOverride
        });
    }

    private async refreshPinFrequencyToken(pin: string): Promise<void> {
        const pinFrequencyOption = ExportableFileHelper.getPinFrequencyDocumentTypeByFileType(this.state.exportableFile?.fileType);
        const response = await SignatureClient.refreshPinFrequencyToken(pinFrequencyOption, pin);

        if (response) {
            sessionStore.dispatch(LocalOperations.Login.UpdatePinFrequency, {
                pinFrequencyDocumentType: response.SignaturePinType,
                token: response.Token
            });

            sessionStore.dispatch(LocalOperations.Settings.UpdatePinFrequency, {
                pinFrequencyDocumentType: response.SignaturePinType,
                token: response.Token
            });

            this.mutateState(s => s.isPinFrequencyTokenValidated = true);
        }
    }

    private async loadRadPdfEmployeeFormSign(): Promise<{ isFirstTime: boolean, shouldSign: boolean }> {
        const formId = (this.state.exportableFile as FileTypeRadPdf).formId;
        const actionType = this.props.mode;

        const response = await CustomFaxClient.loadEmployeeFormSign(
            sessionStore.getCurrentEmployee()?.ContactId,
            formId,
            actionType
        );

        if (response) {
            return { isFirstTime: !response.HasRecord, shouldSign: response.ShouldSign };
        }

        return { isFirstTime: true, shouldSign: false };
    }

    private applySignatureToExportableFile(pin: string, isInitialSignButtonClicked: boolean = true): void {
        const pinFrequencyToken = ExportableFileHelper.getPinFrequencyTokenByFileType(
            this.state.exportableFile?.fileType,
            this.props.loginPinFrequencyModel ?? LoginPinFrequencyModel.fromResponseModel(sessionStore.getCurrentEmployee()?.Model?.LoginPinFrequency)
        );

        this.mutateState(s => {
            s.isValidPin = true;
            s.exportableFile = s.exportableFile.clone();
            s.exportableFile.applyOverlayElement(new PrepositionedSignatureOverlayElement({ Pin: pin, Token: pinFrequencyToken }));
            s.exportableFile.clearGeneratedFileWithOverlayCache();

            if (s.hasSignatureAccordingToPreferences) {
                s.isInitialSignButtonClicked = isInitialSignButtonClicked;
                s.hasSignatureAccordingToPreferences = false;
                s.hasBeenSigned = true;
            }
            else {
                if (s.exportableFile.allowedDraggableImageType.contains(DraggableImageOverlayType.Signature)) {
                    s.defaultFilePreviewComponentMode = FilePreviewComponentMode.imageEdit;
                    s.currentPreviewMode = FilePreviewComponentMode.imageEdit;
                }
            }

            if (s.exportableFile) {
                this.props.onValidateSignatureCallback?.(s.exportableFile);
            }

            if (this.getSignaturePreferencesProperty(s.exportableFile)) {
                this.updateUserSignatureSettings(true);
            }
        });
    }

    /**
     * Get whether the logged-in user has a signature.
     */
    private getHasSignOnPrintAccess(): boolean {
        return this.props.hasSignOnPrintAccess ?? sessionStore.signOnPrintAccess;
    }

    /**
     * Get whether the logged-in user has a PIN.
     */
    private getHasPinSet(): boolean {
        return this.props.hasPinSet ?? sessionStore.hasPinSet;
    }

    private userCanSign(): boolean {
        return this.getHasSignOnPrintAccess() && this.getHasPinSet();
    }

    private onInitialSignButtonClick(): void {
        const hasSignOnPrintAccess = this.getHasSignOnPrintAccess();
        const canUserSignForm = this.userCanSign();

        this.mutateState(s => {
            s.isInitialSignButtonClicked = true;
            s.isSignatureRemoved = false;

            if (canUserSignForm && s.hasSignatureAccordingToPreferences) {
                s.hasSignatureAccordingToPreferences = false;
                s.hasBeenSigned = true;
            }

            if (s.isPinFrequencyTokenValidated && hasSignOnPrintAccess) {
                this.applySignatureToExportableFile("");
                s.isFilePreviewLoaded = false;
                s.canShowSignatureToObstetricalFileInPreviewMode = true;
            }
            else if (!hasSignOnPrintAccess) {
                this.handleSettingPinAndSignature();
            }
        });
    }

    private handleSettingPinAndSignature(): void {
        sessionStore.dispatchMain(
            LocalOperations.MainComponentViewOperations.DisplaySignatureOptionPopup,
            {
                onSaveSuccess: (pin: string) => {
                    if (this.userCanSign()) {
                        this.mutateState(s => {
                            s.isPinFrequencyTokenValidated = true;
                            s.isFilePreviewLoaded = false;
                            s.canShowSignatureToObstetricalFileInPreviewMode = true;

                            this.applySignatureToExportableFile(
                                pin,
                                this.hasNoSignaturePreferencesOrIsNotRadPdf()
                            );
                        });
                    }
                }
            }
        );
    }

    private async getExportableFileOnEditMode(generationIntent: ExportableFileGenerationIntent = ExportableFileGenerationIntent.Preview): Promise<ExportableFile> {
        const validFileTypes = [FileType.RADPDF, FileType.OBSTETRICALFILE];
        let cleanExportableFile = this.state.exportableFile;

        if (
            this.state.currentPreviewMode === FilePreviewComponentMode.imageEdit
            && cacheStore.ClinicSettings.EnableQuickDocumentSigning
            && validFileTypes.any(validFileType => validFileType === cleanExportableFile?.fileType)
        ) {
            const overlayElement = this.filePreviewComponentEditable?.getCurrentOverlay();
            cleanExportableFile = cleanExportableFile.getCleanCopy();

            if (overlayElement?.length > 0) {
                overlayElement.forEach(p => {
                    cleanExportableFile.applyOverlayElement(p);
                });

                cleanExportableFile.saveOverlayPositionsIfNeeded();
            }

            const fetchUrlOnEditMode = (await cleanExportableFile.getFetchUrlDtoAsync(true, false, false, false, false, null, generationIntent))?.FetchUrl;

            this.mutateState(state => {
                state.hasBeenSigned = overlayElement?.length > 0;
                state.exportableFile = cleanExportableFile;
                state.latestGeneratedFetchUrl = fetchUrlOnEditMode;
            });
        }

        return cleanExportableFile;
    }

    private isSignButtonDisable(): boolean {
        const isInEditMode = this.state.currentPreviewMode === FilePreviewComponentMode.imageEdit;
        const isFileLoading = isInEditMode ? !this.state.isFileEditLoaded : !this.state.isFilePreviewLoaded;

        if (cacheStore.ClinicSettings.EnableQuickDocumentSigning) {
            return (
                this.state.isSignButtonDisabled
                || isFileLoading
                || !this.state.isPinFrequencyTokenValidated
                || (!this.state.exportableFile?.hasSignature() && !this.state.hasBeenSigned)
                || !this.userCanSign()
            );
        }
        else {
            return this.state.isSignButtonDisabled || isInEditMode || !this.state.exportableFile?.hasSignature();
        }
    }

    private isPrintButtonDisable(): boolean {
        const isInEditMode = this.state.currentPreviewMode === FilePreviewComponentMode.imageEdit;
        const isFileLoading = isInEditMode ? !this.state.isFileEditLoaded : !this.state.isFilePreviewLoaded;

        return (
            isInEditMode
            && (
                !this.state.isFileEditLoaded
                || !cacheStore.ClinicSettings.EnableQuickDocumentSigning
            )
            || isFileLoading
        );
    }

    private getSignaturePreferencesProperty(exportableFile: ExportableFile): "prescription" | "examRequest" | "consultationRequest" {
        const exportableFileType = exportableFile?.fileType ?? this.state.exportableFile?.fileType;
        switch (exportableFileType) {
            case FileType.Prescription:
                return "prescription";

            case FileType.EXAMREQUEST:
                return "examRequest";

            case FileType.CONSULTATIONREQUEST:
                return "consultationRequest";

            default:
                return null;
        }
    }

    private hasSignatureAccordingToPreferences(currentAction: ExportMode, exportableFile: ExportableFile): boolean {
        if (!cacheStore.ClinicSettings.EnableQuickDocumentSigning)
            return false;

        const settingsByFileType = this.getSignaturePreferencesProperty(exportableFile);
        if (!settingsByFileType)
            return false;

        const settings = this.state.exportableFilePopupSetting[settingsByFileType]?.exportableFileSignatureSettings;
        return settings?.firstOrDefault(p => p.actionType === currentAction)?.shouldSign == true;
    }

    private handleOverlaysChange(draggableOverlays: DraggableImageOverlayElement[], prepositionedSignatureOverlays: PrepositionedSignatureOverlayElement[]): void {
        const hasDraggableOverlays = draggableOverlays?.any();
        const hasPrepositionedSignatureOverlays = prepositionedSignatureOverlays?.any();
        if (!hasDraggableOverlays && !hasPrepositionedSignatureOverlays && this.state.saveSignatureChecked) {
            this.onSaveSignatureChanged();
        }
        this.mutateState(s => {
            if (this.state.isPreviewMode) {
                s.isSaveSignaturePositionDisabled = !this.state.exportableFile.hasSignature();
                s.isSignButtonDisabled = !this.state.exportableFile.hasSignature();
            }
            else {
                if (this.state.currentPreviewMode === FilePreviewComponentMode.imageEdit) {
                    s.isSaveSignaturePositionDisabled = !hasDraggableOverlays;
                    s.isSignButtonDisabled = !hasDraggableOverlays;
                    s.hasBeenSigned = hasDraggableOverlays;
                }
                else {
                    s.isSaveSignaturePositionDisabled = !hasDraggableOverlays && !s.hasBeenSigned;
                    s.isSignButtonDisabled = !hasDraggableOverlays && !s.hasBeenSigned;
                }
            }
        });
    }

    private hasSignedBeforeAndCanSign(): boolean {
        return this.userCanSign() && !this.state.isFirstTime && this.state.shouldSign;
    }

    private hasNoSignaturePreferencesOrIsNotRadPdf(): boolean {
        return (
            !this.state.hasSignatureAccordingToPreferences
            || this.state.exportableFile.fileType !== FileType.RADPDF
        );
    }

    private hasPreferenceAndNotSetupToSignYet(): boolean {
        return (
            cacheStore.ClinicSettings.EnableQuickDocumentSigning
            && this.state.hasSignatureAccordingToPreferences
            && (!this.userCanSign() || !this.state.isPinFrequencyTokenValidated)
        );
    }

    private hasSignedCurrentRadPdfFormBefore(): boolean {
        return (
            this.state.exportableFile.fileType === FileType.RADPDF
            && !this.state.isFirstTime
            && this.state.shouldSign
        );
    }

    private hasClickedSignButtonInObstetricalFile(): boolean {
        return (
            this.state.exportableFile.fileType === FileType.OBSTETRICALFILE
            && this.state.isInitialSignButtonClicked
            && this.state.canShowSignatureToObstetricalFileInPreviewMode
        );
    }

    private updateExportableFilePopupSetting(exportableFilePopupSetting: ExportableFilePopupSettingModel): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.UpdateUserSettingsExportableFilePopup, {
            settings: exportableFilePopupSetting
        });
    }

    private async onRemoveSignatureButtonClick(): Promise<void> {
        const exportableFile = await this.filePreviewComponentEditable.removeSignature();

        this.mutateState(s => {
            s.showSpinningWheel = true;
            s.exportableFile = exportableFile;
            s.isSignatureRemoved = true;
            this.props.onValidateSignatureCallback?.(s.exportableFile);
        }, async () => {
            const pinFrequencyTokenValid = await this.validatePinFrequencyToken(this.state.exportableFile);

            this.mutateState(s => {
                s.isPinFrequencyTokenValidated = pinFrequencyTokenValid;
                s.isInitialSignButtonClicked = !pinFrequencyTokenValid;
                s.isValidPin = pinFrequencyTokenValid;
            });
        });
        this.updateUserSignatureSettings(false);
    }

    private async updateUserSignatureSettings(isSigned: boolean, action?: ExportMode): Promise<void> {
        this.mutateState(s => {
            const settingsByFileType = this.getSignaturePreferencesProperty(this.state.exportableFile);
            const actionType = IsNullOrUndefined(action) ? this.props.mode : action;

            if (settingsByFileType) {
                const settings = this.state.exportableFilePopupSetting[settingsByFileType]?.exportableFileSignatureSettings;
                if (settings?.length > 0) {
                    const signatureUserSetting = s.exportableFilePopupSetting[settingsByFileType].exportableFileSignatureSettings
                        .filter(p => p.actionType === actionType);

                    if (signatureUserSetting.length > 0)
                        signatureUserSetting[0].shouldSign = isSigned;
                    else
                        s.exportableFilePopupSetting[settingsByFileType].exportableFileSignatureSettings
                            .push(new ExportableFileSignatureSettingsModel({
                                actionType: actionType,
                                shouldSign: isSigned
                            }));
                }
                else {
                    s.exportableFilePopupSetting[settingsByFileType].exportableFileSignatureSettings = [new ExportableFileSignatureSettingsModel({
                        actionType: actionType,
                        shouldSign: isSigned
                    })];
                }
            }
        });
    }

    private async validatePinFrequencyToken(exportableFile: ExportableFile): Promise<boolean> {
        const pinFrequencyModel = this.props.loginPinFrequencyModel ?? LoginPinFrequencyModel.fromResponseModel(sessionStore.getCurrentEmployee()?.Model?.LoginPinFrequency);
        const pinFrequencyToken = ExportableFileHelper.getPinFrequencyTokenByFileType(exportableFile?.fileType, pinFrequencyModel);

        return await ExportableFileHelper.validatePinFrequencyToken(pinFrequencyToken);
    }

    private async saveUpdatedRecipient(): Promise<void> {
        if (this.state.nextRecipient == null)
            return;

        const MedicalRequestId = this.getMedicalRequestId();

        if (typeof this.state.nextRecipient === "string"
            && this.state.nextRecipient === this.props.selectedRecipient)
            return;

        if (typeof this.state.nextRecipient !== "string"
            && typeof this.props.selectedRecipient !== "string"
            && this.state.nextRecipient?.id === this.props.selectedRecipient?.id)
            return;

        let RecipientContactDirectoryEntityId: string | null;
        let RecipientContactDirectoryEntityType: DirectoryEntityType | null;
        let Recipient: string | null;

        if (typeof this.state.nextRecipient === "string") {
            Recipient = this.state.nextRecipient;
        }
        else {
            Recipient = this.state.nextRecipient.getDisplayName();

            RecipientContactDirectoryEntityId = this.state.nextRecipient?.id;
            RecipientContactDirectoryEntityType = this.state.nextRecipient?.type;
        }

        if (RecipientContactDirectoryEntityId != null || Recipient != null) {
            const response = await Client.postAsync<GenericResponse>(
                ServerOperations.Server.Myle_Patient_MedicalNote_ModifyMedicalRequestRecipient,
                {
                    MedicalRequestType: this.state.exportableFile.fileType === FileType.CONSULTATIONREQUEST
                        ? MedicalRequestType.ConsultationRequest
                        : MedicalRequestType.LabRequest,
                    MedicalRequestId,
                    Recipient,
                    RecipientContactDirectoryEntityId,
                    RecipientContactDirectoryEntityType
                }
            );

            if (!response.errors.any())
                this.updateMedicalNoteCache(MedicalRequestId, {
                    Recipient,
                    RecipientContactDirectoryEntityId,
                    RecipientContactDirectoryEntityType
                });
        }
    }

    private updateMedicalNoteCache(
        medicalRequestId: string,
        nextRecipient: {
            Recipient: string,
            RecipientContactDirectoryEntityId: string,
            RecipientContactDirectoryEntityType: DirectoryEntityType
        }
    ): void {
        if (this.state.exportableFile.fileType === FileType.CONSULTATIONREQUEST) {
            const requests = patientStore.PatientMedicalNoteModels[this.props.patientContactId]?.get("ConsultationRequests");
            const consultationRequest = requests?.find(p => p.ConsultationRequestId === medicalRequestId);

            if (consultationRequest) {
                consultationRequest.Recipient = nextRecipient.Recipient;
                consultationRequest.RecipientContactDirectoryEntityId = nextRecipient.RecipientContactDirectoryEntityId;
                consultationRequest.RecipientContactDirectoryEntityType = nextRecipient.RecipientContactDirectoryEntityType;

                sessionStore.dispatch(LocalPatientOperations.MedicalNote.ConsultationRequestPopup_OnSaveSucces, {
                    item: consultationRequest,
                    patientContactId: this.props.patientContactId,
                    shouldSaveRelatedEmployee: false
                });
            }
        }
        else {
            const requests = patientStore.PatientMedicalNoteModels[this.props.patientContactId]?.get("Requests");
            const labRequest = requests?.find(p => p.RequestId === medicalRequestId);

            if (labRequest) {
                labRequest.Recipient = nextRecipient.Recipient;
                labRequest.RecipientContactDirectoryEntityId = nextRecipient.RecipientContactDirectoryEntityId;
                labRequest.RecipientContactDirectoryEntityType = nextRecipient.RecipientContactDirectoryEntityType;

                sessionStore.dispatch(LocalPatientOperations.MedicalNote.LabRequestPopup_OnSaveSucces, {
                    item: labRequest,
                    patientContactId: this.props.patientContactId,
                    shouldSaveRelatedEmployee: false
                });
            }
        }
    }

    private getMedicalRequestId(): string {
        return (this.state.exportableFile.fileType === FileType.CONSULTATIONREQUEST
            ? (this.state.exportableFile as FileTypeConsultationRequest).ConsultationRequestId
            : (this.state.exportableFile as FileTypeExamRequest).ExamRequestId);
    }

    private hasCoverSheetSection(): boolean {
        return cacheStore.ClinicSettings.EnableFaxCoverSheet
            && this.faxCoverSheetEditorVisibilityModes.includes(this.props.mode);
    }
}

export { FaxSignPrintExportableFileComponent, FaxSignPrintExportableFileComponentState, FaxSignPrintExportableFileDefaultContext, IFaxPrintExportableFileComponentProps };
